import {
  SensorHourlyDataPoint,
  useSensorUsageDaily as useRealSensorUsageDaily,
} from "common";
import React, { useState } from "react";

import { SensorUptimeChart, SensorRollupData } from "./SensorUptimeChart";

// Handle potential import error by creating a fallback hook
const useFallbackSensorUsageDaily = (_params: any) => ({
  data: null,
  loading: false,
  error: "Failed to load sensor usage data. API module not available.",
  refetch: () => Promise.resolve(),
});

// Use real hook if it's available, otherwise use fallback
const useSensorUsageDaily =
  useRealSensorUsageDaily || useFallbackSensorUsageDaily;

interface SensorUptimeChartConnectorProps {
  sensorId: string | null;
  maxWidth?: number;
}

export const SensorUptimeChartConnector: React.FC<
  SensorUptimeChartConnectorProps
> = ({ sensorId, maxWidth = 1800 }) => {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
  });

  // Add try-catch to handle any errors during hook initialization
  let hookResult;
  try {
    if (typeof useSensorUsageDaily !== "function") {
      throw new Error("Hook function not available");
    }

    hookResult = useSensorUsageDaily({
      sensorId: sensorId || "",
      date: selectedDate,
      enabled: !!sensorId,
    });
  } catch (_err) {
    hookResult = {
      data: null,
      loading: false,
      error: "Error fetching sensor data. The API module may not be available.",
      refetch: () => Promise.resolve(),
    };
  }

  const { data, loading, error, refetch } = hookResult;

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleRefresh = () => {
    refetch();
  };

  // Convert SensorHourlyDataPoint[] to the format expected by SensorUptimeChart
  /**
   * Convert the SensorHourlyDataPoint array to a format compatible with SensorRollupData
   * for the SensorUptimeChart component
   */
  const convertToChartData = (
    data: SensorHourlyDataPoint[] | null,
  ): SensorRollupData[] => {
    if (!data) return [];

    return data.map((item) => {
      // Create a chart-compatible data point with the required fields
      const chartData: SensorRollupData = {
        sensorId: item.sensorId,
        date: item.date,
        dateHour: item.dateHour,
        hour: item.hour,
        sensorType: item.sensorType,
        hubId: item.hubId,
        totalDataPoints: item.totalDataPoints,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };

      return chartData;
    });
  };

  return (
    <SensorUptimeChart
      sensorId={sensorId}
      sensorData={convertToChartData(data)}
      refreshData={handleRefresh}
      isLoading={loading}
      error={error}
      selectedDate={selectedDate}
      onDateChange={handleDateChange}
      maxWidth={maxWidth}
    />
  );
};
